export interface PrivacySection {
  id: string;
  title: string;
  content: string;
}

export const privacyPolicySections: PrivacySection[] = [
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    content: `We may collect the following categories of information:

Account and business verification information.
When you request access to Animind, we may collect your name, business email address, company name, job title, phone number, business address, website, business license, company registration documents, tax or company identification information, and other materials needed to verify your business eligibility.

User and login information.
If your account is approved, we may collect login credentials, account settings, team member information, permission levels, authentication data, and security logs.

Customer content.
When you use Animind, you may submit prompts, scripts, storyboards, character descriptions, images, videos, audio, project files, references, creative instructions, metadata, and other materials. We may also process outputs generated through the platform, including video, image, animation, and related creative assets.

Usage and technical information.
We may automatically collect IP address, browser type, device identifiers, operating system, referring pages, pages viewed, session duration, feature usage, error logs, API activity, approximate location derived from IP address, and other diagnostic or analytics data.

Payment and transaction information.
If paid services are offered, payment information may be processed by third-party payment processors. We generally do not store full card numbers. We may receive limited payment-related information such as transaction ID, payment status, billing details, invoice records, card brand, last four digits, chargeback information, and fraud-risk signals.

Communications.
If you contact us, we may collect the content of your messages, support requests, meeting notes, email correspondence, and related contact information.`
  },
  {
    id: "how-we-use-information",
    title: "2. How We Use Information",
    content: `We use information for the following purposes:

To review business access applications and verify that applicants are legitimate business users.

To create, manage, secure, and administer user accounts.

To provide, operate, maintain, improve, and troubleshoot the Animind platform.

To process prompts, creative instructions, uploaded materials, and customer content in order to generate or modify AI-assisted film, animation, image, video, and related creative outputs.

To maintain project continuity, character consistency, multi-character scene consistency, shot-level control, version history, and other platform functions.

To communicate with you about onboarding, account approval, support, service updates, billing, security, and administrative matters.

To process payments, issue invoices, manage subscriptions or paid credits where applicable, and prevent fraud or misuse.

To monitor platform security, prevent unauthorized access, detect abuse, enforce our terms, and protect our rights and the rights of other users.

To analyze website and product performance, improve user experience, develop new features, and understand customer needs.

To comply with legal, regulatory, tax, accounting, sanctions, export control, and law enforcement obligations.`
  },
  {
    id: "ai-processing",
    title: "3. AI Processing and Customer Content",
    content: `Animind is an AI creation platform. Customer content may be processed by our own systems and by selected third-party AI, cloud, infrastructure, storage, security, analytics, and service providers as necessary to provide the services.

Unless separately agreed in writing, we do not claim ownership of your customer content. Your ownership and usage rights in uploaded materials and generated outputs are governed by our Terms of Service, customer agreement, or other applicable written agreement.

We do not use your confidential customer content to train our own general-purpose AI models unless you have expressly agreed to such use or unless the content has been anonymized or aggregated so that it no longer identifies you, your company, your users, or your projects.

Because some AI processing may depend on third-party model providers or infrastructure providers, customer content may be transmitted to and processed by those providers solely for service delivery, security, abuse prevention, performance improvement, or other purposes permitted under our agreements with them.

You are responsible for ensuring that you have all necessary rights, permissions, and legal bases to upload or submit content to Animind, including any scripts, images, voices, likenesses, third-party copyrighted materials, trademarks, confidential materials, or personal information contained in your inputs.`
  },
  {
    id: "cookies",
    title: "4. Cookies and Similar Technologies",
    content: `We may use cookies, pixels, local storage, and similar technologies to operate the website, remember preferences, secure sessions, analyze traffic, improve performance, and support marketing or attribution activities.

You can control cookies through your browser settings. Blocking certain cookies may affect website functionality or account access.

Where required by law, we will obtain your consent before using non-essential cookies or similar tracking technologies.`
  },
  {
    id: "how-we-share",
    title: "5. How We Share Information",
    content: `We may disclose information to the following categories of recipients:

Service providers.
We may share information with vendors that provide hosting, cloud infrastructure, AI model processing, storage, security, analytics, customer support, email delivery, business verification, payment processing, fraud prevention, accounting, and other operational services.

Payment processors and financial partners.
Payment and billing information may be processed by third-party payment processors, acquiring banks, gateways, fraud-screening providers, and related financial service providers.

Business partners and authorized resellers.
If you access Animind through an authorized partner, reseller, enterprise arrangement, or agency relationship, we may share limited account, usage, billing, or support information with that partner as necessary to manage the relationship.

Legal and compliance recipients.
We may disclose information when required by law, subpoena, court order, regulatory request, tax obligation, sanctions screening, export control requirement, or law enforcement request.

Business transfers.
If we are involved in a merger, acquisition, financing, restructuring, sale of assets, or similar transaction, information may be disclosed or transferred as part of that transaction, subject to appropriate confidentiality protections.

Protection of rights.
We may disclose information when we believe it is necessary to protect Animind, ATELIER TENET INC., our users, our platform, our intellectual property, our security, or the rights and safety of others.

We do not sell personal information in the traditional sense. We also do not knowingly sell or share personal information of children.`
  },
  {
    id: "international-transfers",
    title: "6. International Data Transfers",
    content: `Animind may process and store information in Canada, the United States, Hong Kong, Singapore, the European Economic Area, or other jurisdictions where we, our affiliates, or our service providers operate.

These jurisdictions may have privacy laws that differ from those in your country. Where required, we use appropriate safeguards for cross-border transfers, such as contractual protections, data processing agreements, standard contractual clauses, or other lawful transfer mechanisms.`
  },
  {
    id: "data-retention",
    title: "7. Data Retention",
    content: `We retain personal information and customer content for as long as reasonably necessary to provide the services, maintain accounts, comply with legal and financial obligations, resolve disputes, enforce agreements, prevent fraud or abuse, and maintain security.

Business verification materials may be retained for compliance, audit, fraud prevention, and account integrity purposes.

Customer content may remain in active systems, backups, logs, or archives for a limited period after deletion or account termination, unless a longer retention period is required by law, contract, security, or dispute-resolution needs.

You may request deletion of certain information as described below, subject to legal, contractual, security, and operational limitations.`
  },
  {
    id: "security",
    title: "8. Security",
    content: `We use reasonable administrative, technical, and organizational measures designed to protect information against unauthorized access, loss, misuse, alteration, or disclosure.

These measures may include access controls, encryption in transit, authentication, logging, security monitoring, vendor review, and internal access restrictions.

No system is completely secure. You are responsible for maintaining the confidentiality of your login credentials and for ensuring that your team members use the platform securely.`
  },
  {
    id: "privacy-rights",
    title: "9. Your Privacy Rights",
    content: `Depending on your location, you may have rights to:

Access the personal information we hold about you.

Request correction of inaccurate or incomplete personal information.

Request deletion of personal information.

Object to or restrict certain processing activities.

Request portability of certain personal information.

Withdraw consent where processing is based on consent.

Opt out of certain marketing communications.

Lodge a complaint with a privacy or data protection authority.

To exercise these rights, contact us at privacy@animind.net. We may need to verify your identity and authority before responding.`
  },
  {
    id: "eea-uk-swiss",
    title: "10. Additional Notice for EEA, UK, and Swiss Users",
    content: `If GDPR or similar laws apply, our legal bases for processing may include:

Performance of a contract, including providing access to the Animind platform and related services.

Legitimate interests, including platform security, fraud prevention, service improvement, business communications, analytics, and enforcement of legal rights.

Consent, where required for certain cookies, marketing communications, or optional processing.

Legal obligations, including tax, accounting, regulatory, sanctions, export control, and compliance requirements.

Where we rely on legitimate interests, we balance those interests against your privacy rights and expectations.`
  },
  {
    id: "canadian-users",
    title: "11. Additional Notice for Canadian Users",
    content: `If Canadian privacy law applies, we collect, use, and disclose personal information for purposes that a reasonable person would consider appropriate in the circumstances, including providing B2B AI creation services, account administration, security, billing, compliance, and customer support.

You may request access to or correction of your personal information by contacting us at privacy@animind.net.`
  },
  {
    id: "california-us",
    title: "12. Additional Notice for California and Other U.S. State Residents",
    content: `Depending on applicable law, residents of certain U.S. states may have rights to know, access, correct, delete, or obtain a copy of personal information, and to opt out of certain processing activities such as targeted advertising, sale, sharing, or certain profiling.

In the preceding 12 months, we may have collected the categories of personal information described in this Privacy Policy, including identifiers, business contact information, commercial information, internet or network activity, approximate location, professional or employment-related information, payment-related information, and user-generated content.

We may disclose these categories to service providers, payment processors, infrastructure providers, analytics providers, security vendors, professional advisors, business partners, and legal or compliance recipients for the purposes described in this Privacy Policy.

We do not use sensitive personal information for purposes that require a right to limit use under California law unless we provide a specific notice and choice.

To exercise applicable rights, contact privacy@animind.net.`
  },
  {
    id: "marketing",
    title: "13. Marketing Communications",
    content: `We may send business, product, onboarding, or marketing communications to users who request information, create an account, attend a meeting, or otherwise engage with us.

You may opt out of marketing emails by using the unsubscribe link in the email or contacting us. We may still send transactional, security, billing, account, or legal notices.`
  },
  {
    id: "third-party-links",
    title: "14. Third-Party Links and Services",
    content: `Our website or platform may contain links to third-party websites, tools, integrations, model providers, payment processors, or business services.

We are not responsible for the privacy practices of third parties. Your use of third-party services may be governed by their own terms and privacy policies.`
  },
  {
    id: "children",
    title: "15. Children's Privacy",
    content: `Animind is a B2B service and is not directed to children. We do not knowingly collect personal information from children. If we learn that a child has provided personal information, we will take reasonable steps to delete it.`
  },
  {
    id: "changes",
    title: "16. Changes to This Privacy Policy",
    content: `We may update this Privacy Policy from time to time. The updated version will be posted on this page with a new effective date. If changes are material, we may provide additional notice by email, account notice, or website notice.

Your continued use of Animind after the updated Privacy Policy becomes effective means that you acknowledge the updated policy.`
  },
  {
    id: "contact",
    title: "17. Contact Us",
    content: `For privacy questions, data requests, or complaints, contact:

Animind Privacy Team
Company: ATELIER TENET INC.
Address: 3660 MIDLAND AVE SUITE 326, SCARBOROUGH, ON M1V 0B8, CANADA
Email: privacy@animind.net
Website: https://www.animind.net`
  }
];

export const privacyPolicyMeta = {
  effectiveDate: "[Month Day, Year]",
  website: "https://www.animind.net",
  company: "ATELIER TENET INC.",
  address: "3660 MIDLAND AVE SUITE 326, SCARBOROUGH, ON M1V 0B8, CANADA",
  contact: "privacy@animind.net"
};
